import { Box, Stack, Typography } from '@dtx-company/design-system/src'
import { ColorInputHex } from '@dtx-company/shared-components/src'
import { ColorLensOutlined } from '@mui/icons-material'
import { ColorSelectionMethods } from '@app/common/src/types/ColorSelectionMethods'
import { EditTheme } from './types'
import { FC, useCallback } from 'react'
import { LockedFeatureWrapper } from '@app/common/src/components/LockedFeatureWrapper/LockedFeatureWrapper'
import { PageType } from '@dtx-company/flow-codegen/src/page/typeUtils'
import { SelectorFrame } from './SelectorFrame'
import { UPDATED_FLOWCODE_COLOR_OPTIONS } from '@app/code/src/constants/flowcode'
import { stopPropagationEvents } from '../PageEditor/components/LinkEditDrawer/LinkEditDrawer.utils'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCurrentPagePermissions } from '../../../utils/useGetCurrentPagePermissions'
import { useDebouncedCallback } from 'use-debounce'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowpage'
import useTranslation from 'next-translate/useTranslation'

interface CardProps {
  page?: PageType
  handleSelect: (values: EditTheme) => void
  curr: string
  color?: string
  contrastText?: string
  mobile?: boolean
  disabled?: boolean
}

export const ColorCard: FC<CardProps> = ({ handleSelect, curr, mobile, disabled }) => {
  const { isViewer, hasOperation } = useAuthState() // org-level permission.
  const { hasViewOnlyAccess } = useCurrentPagePermissions() // page-level permission
  const userCanEditColor = !(isViewer || hasViewOnlyAccess)
  const { t } = useTranslation('codes-pages')
  const setThemeColorDebounced = useDebouncedCallback(
    (color: string) => handleSelect({ primaryColor: color }),
    500
  )

  const setColor = useCallback(
    (color: string, colorName: string, colorSelectedVia: ColorSelectionMethods) => {
      setThemeColorDebounced(color.toUpperCase())
      const eprops = {
        colorName,
        colorHex: color.toUpperCase(),
        colorSelectedVia: colorSelectedVia as string
      }

      events.Flowpage_PageProfile_Selected_Color(eprops)
    },
    [setThemeColorDebounced]
  )

  const userHasHexColors = hasOperation('view:hex_color_page')
  const disabledOptions = !userCanEditColor || disabled
  return (
    <Stack>
      <Stack sx={{ mb: { xs: 2, lg: 4 } }} direction="row" alignItems="center" spacing={{ md: 2 }}>
        <ColorLensOutlined sx={{ display: { xs: 'none', md: 'flex' }, fontSize: 16 }} />
        <Typography
          data-testid="color-card-label"
          variant={{ xs: 'bodyMedium', md: 'subtitleSmall' }}
        >
          {t('CodesPagesDesign.SelectColor')}
        </Typography>
      </Stack>
      <Stack sx={{ flexDirection: 'row', mb: 4 }} {...stopPropagationEvents}>
        {userHasHexColors ? (
          <ColorInputHex
            color={curr}
            onColorChange={selectedColor =>
              setColor(selectedColor, 'hexColor', ColorSelectionMethods.HEX_PICKER)
            }
            disabled={disabledOptions}
            width={{ _: '100%', md: '144px' }}
          />
        ) : (
          <LockedFeatureWrapper featureName="view:hex_color_page">
            <ColorInputHex
              color={curr}
              onColorChange={() => undefined}
              disabled={true}
              width={{ _: '100%', md: '144px' }}
            />
          </LockedFeatureWrapper>
        )}
      </Stack>
      <Box sx={{ mb: 2 }} color="onSurface.secondary">
        <Typography variant="labelMedium">Default Colors</Typography>
      </Box>
      <Stack
        sx={theme => ({
          flexDirection: 'row',
          flexWrap: 'wrap',
          maxWidth: theme.spacing(150),
          justifyContent: 'flex-start'
        })}
      >
        {UPDATED_FLOWCODE_COLOR_OPTIONS.map(colorObj => {
          const selected = curr === colorObj.color
          return (
            <Box
              key={colorObj.color}
              onClick={() => {
                !disabledOptions &&
                  setColor(colorObj.color, colorObj.id, ColorSelectionMethods.COLOR_CHIP)
              }}
              sx={theme => ({
                display: 'flex',
                position: 'relative',
                backgroundColor: `${colorObj.color}`,
                borderRadius: '50%',
                width: { xs: theme.spacing(10), lg: theme.spacing(12) },
                height: { xs: theme.spacing(10), lg: theme.spacing(12) },
                margin: { xs: theme.spacing(2), md: theme.spacing(2, 3) },
                opacity: disabledOptions ? 0.3 : 1,
                cursor: disabledOptions ? 'default' : 'pointer'
              })}
              data-testid={`color-${colorObj.color}`}
              {...stopPropagationEvents}
            >
              {selected && <SelectorFrame />}
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}
